import { ForceDirectedLayoutType } from '@neo4j-ndl/react/nvl';
import type { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { difference, isNil, keyBy } from 'lodash-es';

import type { Scene } from '../../types/scene';
import { isFalsy } from '../../types/utility';
import { REHYDRATE } from '../persistence/constants';
import { NAME, SCENE_EDIT_MODE_EDITING } from './scene.selectors';
import type { ProvidedGraph, SceneState } from './scene.types';
import { deleteSceneThunk } from './thunks/delete-scene';
import { fetchScenesThunk } from './thunks/fetch-scenes';
import { storeSceneThunk } from './thunks/store-scene';
import { updateSceneThunk } from './thunks/update-scene';

export const initialState: SceneState = {
  scenes: {},
  currentNodePositions: [],
  sceneLoadingStatus: 'idle',
  editMode: SCENE_EDIT_MODE_EDITING,
  startWithNewScene: false,
  startDeepLinkedScene: null,
  showViewingModeMessage: true,
  storingRequests: [],
};

/**
 * Reducer and helpers
 */

const sceneSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    setScenes(state, action: PayloadAction<Scene[]>) {
      const newScenes = action.payload;
      state.scenes = keyBy(newScenes, 'id');
    },
    setCurrentSceneId(state, action: PayloadAction<Scene['id']>) {
      state.currentSceneId = action.payload;
    },
    setSceneLoadingStatus(state, action: PayloadAction<SceneState['sceneLoadingStatus']>) {
      state.sceneLoadingStatus = action.payload;
    },
    setCurrentNodePositions(state, action: PayloadAction<SceneState['currentNodePositions']>) {
      state.currentNodePositions = action.payload;
    },
    setEditMode(state, action: PayloadAction<SceneState['editMode']>) {
      state.editMode = action.payload;
    },
    resetEditMode(state) {
      state.editMode = SCENE_EDIT_MODE_EDITING;
    },
    addScene(state, action: PayloadAction<Scene>) {
      const newScene = action.payload;
      if (state.startWithProvidedGraph) {
        const { nodes, relationships } = state.startWithProvidedGraph;
        Object.assign(newScene, {
          numOfNodes: nodes.length,
          numOfRels: relationships.length,
          nodes,
          relationships,
          visualisation: {
            layoutType: ForceDirectedLayoutType,
          },
        });
        state.startWithProvidedGraph = undefined;
      }
      state.scenes[newScene.id] = newScene;
    },
    duplicateScene(state, action: PayloadAction<Scene>) {
      const newScene = action.payload;
      state.scenes[newScene.id] = newScene;
    },
    updateScene(state, action: PayloadAction<Scene>) {
      const scene = action.payload;
      state.scenes[scene.id] = scene;
    },
    updateSceneName(state, action: PayloadAction<{ sceneId: Scene['id']; newName: Scene['name'] }>) {
      const { sceneId, newName } = action.payload;
      const scene = state.scenes[sceneId];
      if (scene) {
        scene.name = newName;
      }
    },
    updateTemporarySceneData(
      state,
      action: PayloadAction<{ sceneId: Scene['id']; temporaryData: Scene['temporaryData'] }>,
    ) {
      const { sceneId, temporaryData } = action.payload;
      const scene = state.scenes[sceneId];
      if (scene) {
        scene.temporaryData = temporaryData;
      }
    },
    updateSceneRoles(state, action: PayloadAction<{ sceneId: Scene['id']; newRoles: Scene['roles'] }>) {
      const { sceneId, newRoles } = action.payload;
      const scene = state.scenes[sceneId];
      if (scene) {
        scene.roles = newRoles;
      }
    },
    updateSceneLastModified(
      state,
      action: PayloadAction<{ sceneId: Scene['id']; lastModified: Scene['lastModified'] }>,
    ) {
      const { sceneId, lastModified } = action.payload;
      const scene = state.scenes[sceneId];
      if (scene) {
        scene.lastModified = lastModified;
      }
    },
    updateSceneGraphCounters(
      state,
      action: PayloadAction<{ numOfNodes: Scene['numOfNodes']; numOfRels: Scene['numOfRels'] }>,
    ) {
      if (isNil(state.currentSceneId)) return;
      const { numOfNodes, numOfRels } = action.payload;
      const scene = state.scenes[state.currentSceneId];
      if (scene) {
        scene.numOfNodes = numOfNodes;
        scene.numOfRels = numOfRels;
      }
    },
    clearScenes(state) {
      state.scenes = {};
      state.currentSceneId = undefined;
    },
    setStartWithNewScene(state, action: PayloadAction<SceneState['startWithNewScene']>) {
      state.startWithNewScene = action.payload;
    },
    setDeepLinkedScene(state, action: PayloadAction<SceneState['startDeepLinkedScene']>) {
      state.startDeepLinkedScene = action.payload;
    },
    setShowViewingModeMessage(state, action: PayloadAction<SceneState['showViewingModeMessage']>) {
      state.showViewingModeMessage = action.payload;
    },
    setStartWithProvidedGraph(state, action: PayloadAction<ProvidedGraph | undefined>) {
      state.startWithProvidedGraph = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: AnyAction) => {
      const { payload } = action;
      if (isFalsy(payload?.[NAME])) return state;
      return {
        ...state,
        ...payload[NAME],
      };
    });
    builder.addCase(fetchScenesThunk.fulfilled, (state, action) => {
      const newScenes = action.payload;
      state.scenes = keyBy(newScenes, 'id');
    });
    builder.addCase(deleteSceneThunk.fulfilled, (state, action) => {
      const sceneId = action.payload;
      delete state.scenes[sceneId];
      if (sceneId === state.currentSceneId) state.currentSceneId = undefined;
    });
    builder.addCase(updateSceneThunk.fulfilled, (state, action) => {
      const { id, numOfNodes, numOfRels, lastModified } = action.payload;
      const scene = state.scenes[id];
      if (scene) {
        scene.lastModified = lastModified;
        scene.numOfNodes = numOfNodes;
        scene.numOfRels = numOfRels;
      }
    });
    builder.addCase(storeSceneThunk.pending, (state, action) => {
      const requestId = action.meta.arg.id;
      state.storingRequests.push(requestId);
    });
    builder.addCase(storeSceneThunk.fulfilled, (state, action) => {
      const { scene: newScene, requestId: oldId } = action.payload;
      delete state.scenes[oldId];

      state.scenes[newScene.id] = newScene;
      state.storingRequests = difference(state.storingRequests, [oldId]);
      if (state.currentSceneId === oldId) {
        state.currentSceneId = newScene.id;
      }
    });
    builder.addCase(storeSceneThunk.rejected, (state, action) => {
      if (action.payload) {
        state.storingRequests = difference(state.storingRequests, [action.payload.requestId]);
      }
    });
  },
});

export const {
  setScenes,
  setCurrentSceneId,
  setCurrentNodePositions,
  setSceneLoadingStatus,
  setEditMode,
  resetEditMode,
  addScene,
  duplicateScene,
  updateScene,
  updateSceneName,
  updateTemporarySceneData,
  updateSceneRoles,
  updateSceneLastModified,
  updateSceneGraphCounters,
  clearScenes,
  setStartWithNewScene,
  setDeepLinkedScene,
  setShowViewingModeMessage,
  setStartWithProvidedGraph,
} = sceneSlice.actions;

export default sceneSlice.reducer;
